import React from 'react';
import withMainLayout from 'layouts/main';
import dynamic from 'next/dynamic';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {SectionPadding, SectionPaddingHalfTop, SectionPaddingHalfBottom} from '@/section';
import {H1, H2, P, PMarginSm} from '@/typography';
import {BreakVisibleXxl} from '@/breaks';
import {withGreen800, withBgNeutral200Rounded} from '@/hocs/withColors';
import {withMarginBottomZeroToMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import {ListOlNumbered} from '@/ui/lists/list-icons';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';

const H1Green800 = withGreen800(H1);
const DivBgNeutral200Rounded = withBgNeutral200Rounded('div');
const DivMarginedMobile = withMarginBottomZeroToMd('div');
const imgPath = '/static/images/developers/video-interview/';

const SubmitPartnerApplication = dynamic(
  import('@/content/developers/submit-partner-application'),
  {
    loading: () => null
  }
);

const seoMetas = {
  title: 'Video interview tool integration | Workable developer partners',
  description:
    'Integrate your video interviewing platform with Workable. Help your customers set up and manage video interviews directly through their recruiting pipeline.',
  canonical: 'https://www.workable.com/developers/partner-program/video-interview'
};

const VideoInterviewPartnerPage = props => (
  <>
    <SectionPaddingHalfBottom>
      <Container variant="section-padding-below-nav">
        <SectionPaddingHalfTop>
          <Grid fluid>
            <Row middle="lg">
              <Col xs={12} lg={6}>
                <H1Green800 className="heading-1-smaller">
                  Connect your video
                  <BreakVisibleXxl /> interviewing tool with Workable
                </H1Green800>
                <PMarginSm>
                  Companies use Workable to set up interviews and evaluate candidates. Connect your
                  system with Workable to make video interviews an easy part of their hiring
                  process.
                </PMarginSm>
                <PMarginSm>
                  Workable helps track candidates through the entire hiring journey. When candidates
                  move to the Phone Screen or Interview stage, users set up interviews and create
                  structured interview questions.
                </PMarginSm>
              </Col>
              <Col xs={12} lg={6}>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="video-interviews-hero"
                  imageTypeFallback="jpg"
                  alt="Connect your video interviewing tool with Workable - image"
                  fetchpriority="high"
                  loading="eager"
                  width={584}
                  height={624}
                />
              </Col>
            </Row>
          </Grid>
        </SectionPaddingHalfTop>
      </Container>
    </SectionPaddingHalfBottom>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-video-interviews"
                  imageTypeFallback="jpg"
                  alt="How customers connect Workable with video interview tools - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <H2 className="heading-3">
                How customers connect Workable with video interview tools
              </H2>
              <PMarginSm>It's easy for Workable users to activate your integration:</PMarginSm>
              <DivMarginedMobile>
                <DivBgNeutral200Rounded>
                  <ListOlNumbered>
                    <li>They find it in the integrations list inside Workable</li>
                    <li>Existing users of both services follow simple on-screen instructions</li>
                    <li>New users see your logo, a description, and a link to learn more</li>
                  </ListOlNumbered>
                </DivBgNeutral200Rounded>
              </DivMarginedMobile>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={6}>
              <H2 className="heading-3">Setting up a video interview in Workable</H2>
              <PMarginSm>
                After connecting, a Workable admin or hiring manager can add video interviews when
                setting up a job:
              </PMarginSm>
              <DivBgNeutral200Rounded>
                <ListOlNumbered>
                  <li>Pick the right stage in the hiring process</li>
                  <li>Choose your service</li>
                  <li>Save changes</li>
                </ListOlNumbered>
              </DivBgNeutral200Rounded>
            </Col>
            <Col xs={12} lg={6}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="setting-up-video-interview"
                  imageTypeFallback="jpg"
                  alt="Setting up a video interview in Workable - image"
                />
              </GridItem>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-video-interviews-candidate"
                  imageTypeFallback="png"
                  altImage="How customers connect Workable with assessment tools - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <PMarginSm>
                When candidates reach that stage, setting up a video interview takes just one click,
                without leaving Workable.
              </PMarginSm>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={5}>
              <PMarginSm>
                When the interview is done, results appear on the candidate's timeline in Workable.
                Users can also see results on your own site.
              </PMarginSm>
            </Col>
            <Col xs={12} lg={6} lgOffset={1}>
              <GridLayout Col={1} autoRows>
                <GridItem>
                  <BoxGrid
                    backgroundColor="var(--brown50)"
                    Url="https://workable.readme.io/"
                    UrlCopy="View API documentation"
                    target="_blank"
                    minHeight={288}>
                    <PMarginSm className="heading-3">API docs</PMarginSm>
                    <P>
                      See how you could start to integrate your product or service with Workable
                    </P>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SubmitPartnerApplication />
  </>
);

export default withMainLayout(VideoInterviewPartnerPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
